import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  notification,
} from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import routes from "../../../../engine/routes";
import {
  MERGE_STATUS,
  QA_CHECKLIST,
  fmtDate,
} from "../institutionMerge.constants";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { MergeJournal } from "./MergeJournal";
import { MergeRestore } from "./MergeRestore";
import { MergeDetach } from "./MergeDetach";
import { MergeDecisions } from "./MergeDecisions";
import { MergeDocuments } from "./MergeDocuments";
import { CountsSnapshot, MergeAudit } from "./MergeAudit";
import "../merge.scss";

const { Title, Text, Paragraph } = Typography;

/** One merge: what was decided, what verification found, who signed off, restore, finalize. */
export function MergeDetail() {
  const { mergeId: id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qa, setQa] = useState([]);
  const [note, setNote] = useState("");
  const [confirmFinal, setConfirmFinal] = useState("");
  const [tab, setTab] = useState("verify");
  const [journalFilters, setJournalFilters] = useState(null);
  // a number in the verification table is a door into the journal, filtered to that field
  const openJournal = key => {
    const [model, ...rest] = key.split(".");
    setJournalFilters({
      model,
      field:
        rest.length && !/_doc$/.test(rest.join("."))
          ? rest.join(".")
          : undefined,
      phase: undefined,
    });
    setTab("journal");
  };
  // a journal row number (from a « fiche complète ») opens the journal on that row's documents
  const openJournalSeq = seq => {
    setJournalFilters({ seq, openedAt: Date.now() });
    setTab("journal");
  };

  const load = useCallback(async () => {
    try {
      setM(await mergeApi.get(id));
    } catch (e) {
      notification.error({
        message: "Fusion introuvable",
        description: errMsg(e),
      });
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  // an interrupted apply / restore can be taken over once its lease is stale: recompute every 30 s
  const [now, setNow] = useState(Date.now());
  const running = !!m && ["applying", "restoring"].includes(m.status);
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [running, m]);
  if (!m) return null;

  const run = async (label, fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      notification.success({
        message: label,
        description: ok ? ok(r) : undefined,
      });
      await load();
    } catch (e) {
      notification.error({
        message: `${label} — échec`,
        description: (
          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{errMsg(e)}</pre>
        ),
        duration: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  const v = m.verification;
  const st = m.status;
  const canRestore = ["applied", "verified", "validated", "failed"].includes(
    st,
  );
  const target = m.targetInstitutionId;
  // « Reprendre » follows what failed or stopped: the apply, a restore or a detach (server: error.during)
  const stuck = st === "applying" || st === "restoring";
  const during =
    st === "restoring" ? "restore" : st === "failed" ? m.error?.during : null;
  const resumeLabel =
    during === "restore"
      ? "Reprendre la restauration"
      : during === "detach"
      ? "Reprendre le détachement"
      : "Reprendre";
  const resumeText =
    during === "restore"
      ? "Le journal garde ce qui a déjà été remis en place ; « Reprendre la restauration » termine la restauration avec les choix enregistrés (rattachements, acquittements), sans rien rejouer deux fois."
      : during === "detach"
      ? "Le journal garde ce qui a déjà été rendu à l'établissement ; « Reprendre le détachement » termine le détachement avec les choix enregistrés, sans rien rejouer deux fois."
      : "Le journal garde ce qui a été fait ; « Reprendre » rejoue ce qui reste, de façon idempotente.";
  // lease: last activity + 15 min without activity → resumable (server: lock.lastActivityAt / staleAfter / stale)
  const staleAfterMs = m.lockStaleAfterMs || 15 * 60 * 1000;
  const lastActivity = m.lock?.lastActivityAt || m.lockedAt || m.updatedAt;
  const staleAt = m.lock?.staleAfter
    ? new Date(m.lock.staleAfter)
    : lastActivity
    ? new Date(new Date(lastActivity).getTime() + staleAfterMs)
    : null;
  const lockIsStale =
    !!m.lock?.stale ||
    !!m.lockStale ||
    (staleAt ? now >= staleAt.getTime() : true);
  const idleMin = lastActivity
    ? Math.max(0, Math.floor((now - new Date(lastActivity).getTime()) / 60000))
    : null;
  const hhmm = d =>
    d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  // a red verification blocks validation and finalization until it is fixed and re-verified
  const verifyRed = !!v && v.ok !== true;
  const notGreenMsg =
    "La vérification n'est pas verte : corrigez puis relancez la vérification";
  // a restore running / failed half-way makes the stored verification obsolete
  const verifyObsolete =
    st === "restoring" || (st === "failed" && m.error?.during === "restore");
  const detachedIds = new Set((m.detached || []).map(d => String(d.sourceId)));
  const remainingSources = (m.sourceInstitutionIds || []).filter(
    s => !detachedIds.has(String(s._id)),
  );

  const verifyRows = v
    ? [
        ...v.forward.map(f => ({
          key: `f:${f.key}`,
          what: f.key,
          journalKey: f.key,
          expected: f.expected,
          found: f.found,
          ok: f.expected === f.found,
        })),
        ...v.residual.map(r => ({
          key: `r:${r.key}:${r.sourceId}`,
          what: `${r.key} — encore sur une source`,
          expected: 0,
          found: r.count,
          ok: false,
        })),
      ]
    : [];

  return (
    <div className="merge-page" style={{ padding: "24px 24px 96px" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
      >
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>
            {m.label}
          </Title>
          <Space>
            <Tag color={MERGE_STATUS[st]?.color}>
              {MERGE_STATUS[st]?.label || st}
            </Tag>
            <Text type="secondary">
              créée le {fmtDate(m.createdAt)} par {m.createdBy || "—"}
            </Text>
          </Space>
        </div>
        <Space>
          <Link to={routes.institutions().mergeHistory.path}>
            <Button>Historique</Button>
          </Link>
          {st === "previewed" && (
            <>
              <Link to={`${routes.institutions().merge.path}?id=${m._id}`}>
                <Button type="primary">Reprendre l'assistant</Button>
              </Link>
              <Button
                danger
                loading={busy}
                onClick={() =>
                  Modal.confirm({
                    title: "Abandonner cette fusion ?",
                    content: "Rien n'a été écrit dans la base.",
                    okType: "danger",
                    // the merge is deleted: reloading it would only answer 404 — back to the history
                    onOk: async () => {
                      setBusy(true);
                      try {
                        await mergeApi.discard(m._id);
                        notification.success({ message: "Fusion abandonnée" });
                        navigate(routes.institutions().mergeHistory.path);
                      } catch (e) {
                        notification.error({
                          message: "Abandon — échec",
                          description: errMsg(e),
                          duration: 0,
                        });
                        setBusy(false);
                      }
                    },
                  })
                }
              >
                Abandonner
              </Button>
            </>
          )}
        </Space>
      </Space>

      {st === "failed" && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message={
            during === "restore"
              ? "La restauration a échoué"
              : during === "detach"
              ? "Le détachement a échoué"
              : "L'application de la fusion a échoué"
          }
          description={
            <Space direction="vertical">
              <Text>{m.error?.message}</Text>
              <Text type="secondary">{resumeText}</Text>
              <Button
                loading={busy}
                onClick={() => run(resumeLabel, () => mergeApi.resume(m._id))}
              >
                {resumeLabel}
              </Button>
            </Space>
          }
        />
      )}
      {stuck && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message={`Opération en cours ou interrompue — dernière activité il y a ${
            idleMin == null ? "?" : idleMin
          } min.${
            lockIsStale
              ? " La reprise est possible (15 min sans activité)."
              : ` La reprise sera possible à ${
                  staleAt ? hhmm(staleAt) : "?"
                } (15 min sans activité).`
          }`}
          description={
            <Space direction="vertical">
              <Text type="secondary">
                {st === "restoring"
                  ? "La fusion est restée « en restauration ». Si personne n'est en train de la restaurer, la reprise termine la restauration à partir du journal."
                  : "La fusion est restée « en cours d'application ». Si personne n'est en train de l'appliquer, la reprise rejoue ce qui reste, de façon idempotente."}
              </Text>
              {!lockIsStale && (
                <Text type="secondary">
                  Si quelqu'un est en train de l'exécuter, elle continue
                  d'écrire dans le journal et ce délai se prolonge.
                </Text>
              )}
              <Button
                loading={busy}
                disabled={!lockIsStale}
                onClick={() => run(resumeLabel, () => mergeApi.resume(m._id))}
              >
                {resumeLabel}
              </Button>
            </Space>
          }
        />
      )}
      {target?.residualOfMerge && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="La cible a été conservée après restauration pour des enregistrements créés après la fusion."
        />
      )}

      <Descriptions bordered size="small" column={2} style={{ marginTop: 16 }}>
        <Descriptions.Item label="Sources">
          {(m.sourceInstitutionIds || []).map(s => {
            const det = (m.detached || []).some(
              d => String(d.sourceId) === String(s._id),
            );
            return (
              <Tag key={s._id} color={det ? "orange" : undefined}>
                {s.name}
                {det ? " (détaché)" : s.archived ? " (archivé)" : ""}
              </Tag>
            );
          })}
        </Descriptions.Item>
        <Descriptions.Item label="Cible">
          {target ? (
            <Space>
              <Link to={routes.institutions(target._id).detail.path}>
                {target.name}
              </Link>
              {st === "restored" && (
                <Tag color="orange">conservée après restauration</Tag>
              )}
            </Space>
          ) : st === "restored" ? (
            <Text type="secondary">supprimée à la restauration</Text>
          ) : (
            <Text type="secondary">pas encore créée</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Référence config">
          {m.configDonorId?.name || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Nom choisi">
          {m.targetMode === "existing" ? (
            <Text type="secondary">
              institution existante — son nom ne change pas
            </Text>
          ) : (
            m.decisions?.targetName || "—"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Appliquée">
          {m.appliedAt ? `${fmtDate(m.appliedAt)} · ${m.appliedBy}` : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Finalisée">
          {m.finalizedAt ? `${fmtDate(m.finalizedAt)} · ${m.finalizedBy}` : "—"}
        </Descriptions.Item>
        {m.journalSummary && (
          <Descriptions.Item label="Journal" span={2}>
            {m.journalSummary.map(j => (
              <Tag
                key={j.key}
                style={{ cursor: "pointer" }}
                onClick={() => openJournal(j.key)}
              >
                {j.key}: {j.count}
              </Tag>
            ))}
          </Descriptions.Item>
        )}
      </Descriptions>
      <MergeAudit merge={m} />

      <Tabs
        style={{ marginTop: 16 }}
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "verify",
            label: "Vérification",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                {v && verifyObsolete ? (
                  <Alert
                    type="info"
                    showIcon
                    message={
                      st === "restoring"
                        ? "Vérification obsolète : une restauration est en cours — relancez la vérification après l'avoir terminée."
                        : "Vérification obsolète : une restauration a échoué — relancez la vérification après l'avoir terminée."
                    }
                    description={`Dernière vérification enregistrée le ${fmtDate(
                      v.at,
                    )}, avant la restauration — son résultat ne décrit plus la base.`}
                  />
                ) : v ? (
                  <Alert
                    type={
                      st === "restored" ? "info" : v.ok ? "success" : "error"
                    }
                    showIcon
                    message={
                      (st === "restored" ? "Avant la restauration — " : "") +
                      (v.ok
                        ? "Vérification réussie — chaque document journalisé tient sa nouvelle valeur, et plus rien ne pointe sur une source."
                        : `${
                            v.missing.length
                          } document(s) n'ont pas la valeur attendue, ${
                            v.residualTotal
                          } pointent encore sur une source, ${v.danglingTotal ||
                            0} pointent encore sur une adresse supprimée par la fusion.`)
                    }
                    description={
                      st === "restored"
                        ? `Vérification enregistrée le ${fmtDate(
                            v.at,
                          )} — avant la restauration.`
                        : `Contrôlée le ${fmtDate(v.at)}`
                    }
                  />
                ) : (
                  <Alert type="info" message="Pas encore vérifiée." />
                )}
                {["applied", "verified", "validated"].includes(st) && (
                  <Button
                    loading={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const r = await mergeApi.verify(m._id);
                        if (r.ok)
                          notification.success({
                            message: "Vérification relancée — tout est vert",
                            description:
                              "0 manquant, 0 résiduel, 0 référence vers une adresse supprimée.",
                          });
                        else
                          notification.warning({
                            message: "Vérification relancée — pas verte",
                            description: `${r.missingTotal ??
                              (r.missing || [])
                                .length} manquant(s), ${r.residualTotal ||
                              0} résiduel(s) sur une source, ${r.danglingTotal ||
                              0} référence(s) vers une adresse supprimée. ${notGreenMsg}.`,
                            duration: 0,
                          });
                        await load();
                      } catch (e) {
                        notification.error({
                          message: "Vérification relancée — échec",
                          description: errMsg(e),
                          duration: 0,
                        });
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Relancer la vérification
                  </Button>
                )}
                {v && !verifyObsolete && (
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={verifyRows}
                    onRow={r => ({
                      onClick: () => r.journalKey && openJournal(r.journalKey),
                      style: { cursor: r.journalKey ? "pointer" : "default" },
                    })}
                    columns={[
                      {
                        title: "Contrôle",
                        dataIndex: "what",
                        render: (w, r) =>
                          r.journalKey ? (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                            >
                              {w} →
                            </Button>
                          ) : (
                            w
                          ),
                      },
                      {
                        title: "Attendu",
                        dataIndex: "expected",
                        align: "right",
                      },
                      { title: "Trouvé", dataIndex: "found", align: "right" },
                      {
                        title: "",
                        dataIndex: "ok",
                        width: 60,
                        render: ok =>
                          ok ? (
                            <Tag color="green">✔</Tag>
                          ) : (
                            <Tag color="red">✗</Tag>
                          ),
                      },
                    ]}
                  />
                )}
                {v && !verifyObsolete && v.missing.length > 0 && (
                  <Table
                    size="small"
                    title={() =>
                      "Documents manquants — nommés, pas seulement comptés"
                    }
                    pagination={{ pageSize: 20 }}
                    rowKey={r => `${r.model}:${r.documentId}`}
                    dataSource={v.missing}
                    columns={[
                      { title: "Collection", dataIndex: "model" },
                      { title: "Champ", dataIndex: "field" },
                      { title: "Enregistrement", dataIndex: "label" },
                      {
                        title: "ID",
                        dataIndex: "documentId",
                        render: x => <Text copyable>{x}</Text>,
                      },
                    ]}
                  />
                )}
                {v &&
                  !verifyObsolete &&
                  (v.danglingTotal > 0 || (v.dangling || []).length > 0) && (
                    <Table
                      size="small"
                      title={() =>
                        `Références vers une adresse supprimée par la fusion — ${v.danglingTotal ||
                          (v.dangling || []).length}${
                          (v.dangling || []).length < (v.danglingTotal || 0)
                            ? ` (les ${
                                (v.dangling || []).length
                              } premières listées)`
                            : ""
                        }`
                      }
                      pagination={{ pageSize: 20, hideOnSinglePage: true }}
                      rowKey="_k"
                      dataSource={(v.dangling || []).map((x, i) => ({
                        ...x,
                        _k: i,
                      }))}
                      columns={[
                        { title: "Collection", dataIndex: "model" },
                        {
                          title: "Enregistrement",
                          render: (_, r) =>
                            r.label ? (
                              r.label
                            ) : r.documentId ? (
                              <Text copyable>{String(r.documentId)}</Text>
                            ) : (
                              <Text type="secondary">
                                {r.count ? `${r.count} document(s)` : "—"}
                              </Text>
                            ),
                        },
                        { title: "Champ", dataIndex: "field" },
                        {
                          title: "Pointe vers",
                          render: (_, r) => {
                            const to =
                              r.addressLabel ??
                              r.pointsToLabel ??
                              r.pointsTo ??
                              r.addressId;
                            const label =
                              to && typeof to === "object"
                                ? to.label || to.name || to._id
                                : to;
                            return (
                              <Text>
                                {label ? `${label} ` : "adresse "}
                                <Text type="secondary">(supprimée)</Text>
                                {r.addressId && (
                                  <Text
                                    type="secondary"
                                    copyable={{ text: String(r.addressId) }}
                                  >
                                    {" "}
                                    …{String(r.addressId).slice(-8)}
                                  </Text>
                                )}
                              </Text>
                            );
                          },
                        },
                      ]}
                    />
                  )}
                {v && !verifyObsolete && (
                  <Table
                    size="small"
                    title={() => "Sur la cible maintenant"}
                    pagination={false}
                    rowKey="key"
                    dataSource={v.target}
                    columns={[
                      { title: "Relation", dataIndex: "label" },
                      { title: "Nombre", dataIndex: "count", align: "right" },
                    ]}
                  />
                )}
              </Space>
            ),
          },
          {
            key: "documents",
            label: "Documents — fiche complète avant / après",
            children: (
              <MergeDocuments mergeId={m._id} onOpenJournal={openJournalSeq} />
            ),
          },
          {
            key: "validate",
            label: "Validation & finalisation",
            children: (
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                {(m.validations || []).length > 0 && (
                  <Table
                    size="small"
                    pagination={false}
                    tableLayout="fixed"
                    rowKey={x => x.at}
                    dataSource={m.validations}
                    columns={[
                      {
                        title: "Par",
                        dataIndex: "by",
                        width: 160,
                        ellipsis: { showTitle: true },
                      },
                      {
                        title: "Le",
                        dataIndex: "at",
                        width: 150,
                        render: fmtDate,
                      },
                      {
                        title: "Note",
                        dataIndex: "note",
                        width: 260,
                        render: n =>
                          n ? (
                            <Text style={{ overflowWrap: "anywhere" }}>
                              {n}
                            </Text>
                          ) : (
                            <Text type="secondary">—</Text>
                          ),
                      },
                      {
                        title: "Checklist",
                        dataIndex: "qaChecklist",
                        width: 90,
                        render: xs =>
                          `${(xs || []).length}/${QA_CHECKLIST.length}`,
                      },
                      {
                        title: "Comptes au moment de la validation",
                        dataIndex: "countsSnapshot",
                        render: x => <CountsSnapshot snapshot={x} />,
                      },
                    ]}
                  />
                )}
                {["applied", "verified", "validated"].includes(st) &&
                  verifyRed && (
                    <Alert
                      type="error"
                      showIcon
                      message={notGreenMsg}
                      description={`Dernière vérification le ${fmtDate(
                        v.at,
                      )} : ${v.missingTotal ??
                        (v.missing || [])
                          .length} manquant(s), ${v.residualTotal ||
                        0} résiduel(s), ${v.danglingTotal ||
                        0} référence(s) vers une adresse supprimée. La validation et la finalisation restent bloquées tant qu'elle n'est pas verte (onglet « Vérification »).`}
                    />
                  )}
                {(st === "verified" || (st === "applied" && v)) && (
                  <Card
                    size="small"
                    title="Contrôle manuel — les vérifications automatiques confirment que les identifiants ont bougé, pas que l'application fonctionne"
                  >
                    <Space direction="vertical">
                      {QA_CHECKLIST.map(q => (
                        <Checkbox
                          key={q.key}
                          checked={qa.includes(q.key)}
                          onChange={e =>
                            setQa(x =>
                              e.target.checked
                                ? [...x, q.key]
                                : x.filter(k => k !== q.key),
                            )
                          }
                        >
                          {q.label}
                        </Checkbox>
                      ))}
                      <Input.TextArea
                        rows={2}
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Remarques (facultatif)"
                      />
                      <Button
                        type="primary"
                        loading={busy}
                        disabled={verifyRed || qa.length < QA_CHECKLIST.length}
                        title={verifyRed ? notGreenMsg : undefined}
                        onClick={() =>
                          run("Fusion validée", () =>
                            mergeApi.validate(m._id, note, qa),
                          )
                        }
                      >
                        Valider ({qa.length}/{QA_CHECKLIST.length})
                      </Button>
                    </Space>
                  </Card>
                )}
                {st === "validated" && (
                  <Card size="small" title="Finaliser">
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Finaliser désarme la restauration. Ne le faites qu'après validation par le client."
                      description="L'historique et le journal sont conservés définitivement — seule la possibilité d'annuler disparaît."
                    />
                    <Space>
                      <Input
                        style={{ width: 320 }}
                        value={confirmFinal}
                        onChange={e => setConfirmFinal(e.target.value)}
                        placeholder={target?.name}
                      />
                      <Button
                        danger
                        loading={busy}
                        disabled={
                          verifyRed ||
                          confirmFinal.trim() !== (target?.name || "").trim()
                        }
                        title={verifyRed ? notGreenMsg : undefined}
                        onClick={() =>
                          Modal.confirm({
                            title: "Finaliser ?",
                            okType: "danger",
                            okText: "Finaliser",
                            cancelText: "Annuler",
                            content: "La restauration ne sera plus possible.",
                            onOk: () =>
                              run("Fusion finalisée", () =>
                                mergeApi.finalize(m._id),
                              ),
                          })
                        }
                      >
                        Finaliser
                      </Button>
                    </Space>
                  </Card>
                )}
                {st === "finalized" && (
                  <Alert
                    type="success"
                    showIcon
                    message="Fusion finalisée. La restauration est désarmée ; l'historique reste consultable ci-dessous."
                  />
                )}
                {st === "restored" && (
                  <Alert type="info" showIcon message="Fusion restaurée." />
                )}
              </Space>
            ),
          },
          ...(canRestore
            ? [
                {
                  key: "restore",
                  label: <span style={{ color: "#cf1322" }}>Restaurer</span>,
                  children: (
                    <Space
                      direction="vertical"
                      size="large"
                      style={{ width: "100%" }}
                    >
                      {st !== "failed" &&
                        (m.sourceInstitutionIds || []).length > 1 && (
                          <Card
                            size="small"
                            title={`Détacher un seul établissement — ${remainingSources.length} encore fusionné(s)`}
                          >
                            {remainingSources.length > 1 ? (
                              <MergeDetach merge={m} onDone={load} />
                            ) : (
                              <Alert
                                type="info"
                                showIcon
                                message="Il ne reste qu'un établissement : utilisez la restauration complète"
                              />
                            )}
                          </Card>
                        )}
                      <Card size="small" title="Restaurer toute la fusion">
                        <MergeRestore merge={m} onDone={load} />
                      </Card>
                    </Space>
                  ),
                },
              ]
            : []),
          {
            key: "journal",
            label: "Journal — avant / après",
            children: (
              <MergeJournal mergeId={m._id} initialFilters={journalFilters} />
            ),
          },
          {
            key: "decisions",
            label: "Décisions & résultat",
            children: (
              <MergeDecisions merge={m} onOpenJournal={openJournalSeq} />
            ),
          },
        ]}
      />
      <Paragraph type="secondary" style={{ marginTop: 16 }}>
        Le journal est conservé définitivement : il répond pour toujours à « à
        quel établissement cette prestation appartenait-elle à l'origine ? ».
      </Paragraph>
    </div>
  );
}
